// src/pages/Margins.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/api';

const inr = v =>
  v == null
    ? '—'
    : Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

function MetricCard({ label, value, sub, highlight }) {
  return (
    <div className="metric-card" style={highlight ? { borderColor: '#38bdf8', borderWidth: 2 } : {}}>
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={highlight ? { color: '#0ea5e9' } : {}}>
        {inr(value)}
      </div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

function SegmentPanel({ title, data }) {
  if (!data) return null;

  const avail   = data.available || {};
  const utilised = data.utilised  || {};

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#e2e8f0', margin: '0 0 0.75rem' }}>
        {title}
      </h2>

      {/* Available */}
      <p style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', margin: '0 0 0.5rem' }}>
        Available
      </p>
      <div className="metrics-grid">
        <MetricCard label="Net Available" value={data.net} highlight />
        <MetricCard label="Cash" value={avail.cash} />
        <MetricCard label="Opening Balance" value={avail.opening_balance} />
        <MetricCard label="Live Balance" value={avail.live_balance} />
        <MetricCard label="Collateral" value={avail.collateral} />
        <MetricCard label="Intraday Payin" value={avail.intraday_payin} />
      </div>

      {/* Utilised */}
      <p style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', margin: '0.75rem 0 0.5rem' }}>
        Utilised
      </p>
      <div className="metrics-grid">
        <MetricCard label="Total Used" value={utilised.debits} />
        <MetricCard label="SPAN Margin" value={utilised.span} />
        <MetricCard label="Exposure" value={utilised.exposure} />
        <MetricCard label="Option Premium" value={utilised.option_premium} />
        <MetricCard label="M2M Realised" value={utilised.m2m_realised} />
        <MetricCard label="M2M Unrealised" value={utilised.m2m_unrealised} />
        <MetricCard label="Portfolio Margin" value={utilised.portfolio_margin} />
        <MetricCard label="Turnover" value={utilised.turnover} />
      </div>

      {/* Utilisation bar */}
      {data.net != null && utilised.debits != null && (data.net + utilised.debits) > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>
            <span>Margin utilisation</span>
            <span>
              {Math.round((utilised.debits / (data.net + utilised.debits)) * 100)}%
            </span>
          </div>
          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.min(100, Math.round((utilised.debits / (data.net + utilised.debits)) * 100))}%`,
                background: '#0ea5e9',
                borderRadius: 3,
                transition: 'width 0.4s',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Margins() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setRefreshMsg(null);
    authFetch('/api/margins')
      .then(r => {
        if (!r.ok) return r.json().then(d => { throw new Error(d.detail || `HTTP ${r.status}`); });
        return r.json();
      })
      .then(d => { setData(d); setLastUpdated(new Date()); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleAutoRefresh = async () => {
    setAutoRefreshing(true);
    setRefreshMsg(null);
    try {
      const res  = await authFetch('/api/user/token/auto-refresh', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Refresh failed');
      setRefreshMsg({ type: 'success', msg: 'Token refreshed! Reloading margin data…' });
      setTimeout(load, 1000);
    } catch (err) {
      setRefreshMsg({ type: 'error', msg: err.message });
    } finally {
      setAutoRefreshing(false);
    }
  };

  const isTokenError = error && (
    error.toLowerCase().includes('api key') ||
    error.toLowerCase().includes('access token') ||
    error.toLowerCase().includes('token') ||
    error.toLowerCase().includes('credentials')
  );

  useEffect(load, [load]);

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">Margins</h1>
          <p className="page-subtitle">
            Live margin availability from Zerodha
            {lastUpdated && (
              <> &mdash; updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</>
            )}
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading} style={{ flexShrink: 0 }}>
          {loading ? <span className="spinner" /> : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {isTokenError ? (
            <div>
              <strong>Access token expired or invalid.</strong>
              <p style={{ margin: '0.4rem 0 0.6rem', fontSize: '0.9rem' }}>
                Zerodha access tokens expire daily at midnight. You need to refresh your token.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={handleAutoRefresh}
                  disabled={autoRefreshing}
                  style={{
                    padding: '0.4rem 1rem', backgroundColor: '#1976d2', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: '0.88rem',
                    fontWeight: 600, cursor: autoRefreshing ? 'default' : 'pointer',
                  }}
                >
                  {autoRefreshing ? 'Refreshing…' : 'Auto-Refresh Token'}
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  style={{
                    padding: '0.4rem 1rem', backgroundColor: 'transparent', color: '#1976d2',
                    border: '1px solid #1976d2', borderRadius: 6, fontSize: '0.88rem',
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Go to Settings
                </button>
              </div>
            </div>
          ) : (
            error
          )}
        </div>
      )}

      {refreshMsg && (
        <div className={`alert alert-${refreshMsg.type === 'success' ? 'success' : 'error'}`}>
          {refreshMsg.msg}
        </div>
      )}

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <span className="spinner" style={{ width: 28, height: 28 }} />
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>Fetching margin data...</p>
        </div>
      )}

      {data && (
        <>
          <SegmentPanel title="Equity" data={data.equity} />
          {data.commodity && data.commodity.net != null && (
            <SegmentPanel title="Commodity" data={data.commodity} />
          )}
        </>
      )}
    </div>
  );
}
